import { useEffect, useRef, useState } from 'react';
import { useSubmissionImages } from '@/hooks/useSubmissions';
import { reportImageLoadFailure } from '@/api/telemetry';
import { isImageProcessingDone } from '@/lib/images';
import { Spinner } from '@/components/ui/Spinner';

/**
 * Small per-submission thumbnail for grid/list rows - falls back to "No preview" until the worker's thumbnail stage completes.
 *
 * The image list is fetched only once the tile is close to the screen. A competition can have hundreds of entries and every
 * tile needs one request (and one database query); fetching them all when the page opens used up the database connections.
 */
export function SubmissionThumbnail({ submissionId, alt }: { submissionId: string; alt: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [nearScreen, setNearScreen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || nearScreen) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNearScreen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNearScreen(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nearScreen]);

  const images = useSubmissionImages(submissionId, nearScreen);
  const image = images.data?.items[0];
  const url = image?.thumbnailUrl ?? image?.previewUrl ?? image?.watermarkedUrl;

  if (!url) {
    // The worker is still producing the preview (the list polls until it exists): show that, not "no preview".
    const processing = nearScreen && (images.isLoading || (image !== undefined && !isImageProcessingDone(image.status)));
    return (
      <div
        ref={ref}
        className="flex h-full w-full flex-col items-center justify-center gap-2 bg-surface-3 text-xs text-fg-muted"
      >
        {processing ? (
          <>
            <Spinner />
            <span>Obdelava slike</span>
          </>
        ) : (
          nearScreen && 'Brez predogleda'
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="h-full w-full">
      <img
        src={url}
        alt={alt}
        loading="lazy"
        onError={() => reportImageLoadFailure('thumbnail', url, submissionId)}
        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
      />
    </div>
  );
}
